/**
 * ExecDIBExplore — Full-page interactive data explorer for state DIB views.
 *
 * Parallel to ExecExplore but with geo-focused defaults: state/CD dimensions,
 * map chart type, location_kind toggle, state filter in sidebar. Metrics are
 * curated for the state-government persona (volume and SB — no speed/competition).
 */
import { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import {
  Globe, LayoutDashboard, Check, ArrowLeftRight, ChevronDown,
  BarChart3, TrendingUp, Grid3x3, Table2, Map as MapIcon, RefreshCw, X,
  AlertCircle, Sparkles,
} from 'lucide-react'
import { usePerformanceQuery } from '../components/exec/generic/usePerformanceQuery'
import BarChartRenderer from '../components/exec/generic/BarChartRenderer'
import LineChartRenderer from '../components/exec/generic/LineChartRenderer'
import HeatmapRenderer from '../components/exec/generic/HeatmapRenderer'
import TableRenderer from '../components/exec/generic/TableRenderer'
import MapChartRenderer from '../components/exec/generic/MapChartRenderer'
import FYRangeSlider from '../components/exec/FYRangeSlider'
import {
  METRIC_LABELS, DIMENSION_LABELS,
  type MetricKey, type GroupByDimension, type ChartType, type LocationKind,
  type GenericWidgetConfig,
  DRILL_HIERARCHY, GEO_DIMENSIONS,
  PSC_L1_NAMES, PSC_L2_NAMES,
} from '../components/exec/generic/types'
import { execAPI } from '../services/exec'
import { trackEvent } from '../services/tracking'

const METRIC_OPTIONS: { value: MetricKey; label: string; group: string }[] = [
  { value: 'total_obligated', label: 'Obligated Dollars', group: 'Volume' },
  { value: 'award_count', label: 'Award Count', group: 'Volume' },
  { value: 'vendor_count', label: 'Unique Vendors', group: 'Volume' },
  { value: 'sb_awards', label: 'SB Awards', group: 'Small Business' },
  { value: 'sb_dollars', label: 'SB Dollars', group: 'Small Business' },
  { value: 'count_8a', label: '8(a) Awards', group: 'Small Business' },
  { value: 'count_hubzone', label: 'HUBZone Awards', group: 'Small Business' },
  { value: 'count_sdvosb', label: 'SDVOSB Awards', group: 'Small Business' },
  { value: 'count_wosb', label: 'WOSB Awards', group: 'Small Business' },
]

const GROUP_BY_OPTIONS: { value: GroupByDimension; label: string }[] = [
  { value: 'state', label: 'State' },
  { value: 'congressional_district', label: 'Congressional District' },
  { value: 'fiscal_year', label: 'Fiscal Year' },
  { value: 'psc_level1_code', label: 'PSC Category' },
  { value: 'psc_level2_code', label: 'PSC Category (L2)' },
  { value: 'color_of_money', label: 'Color of Money' },
]

const DEFAULT_LIMIT = 200
const MAX_LIMIT = 1000
const MIN_LIMIT = 50
const LIMIT_STEP = 50

const CHART_TYPES: { value: ChartType; icon: React.ReactNode; label: string }[] = [
  { value: 'map', icon: <MapIcon size={18} />, label: 'Map' },
  { value: 'bar', icon: <BarChart3 size={18} />, label: 'Bar' },
  { value: 'line', icon: <TrendingUp size={18} />, label: 'Line' },
  { value: 'heatmap', icon: <Grid3x3 size={18} />, label: 'Heatmap' },
  { value: 'table', icon: <Table2 size={18} />, label: 'Table' },
]

const US_STATES: { code: string; name: string }[] = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' }, { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' }, { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' }, { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' }, { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' }, { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' }, { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' }, { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' }, { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' }, { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' }, { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' }, { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' }, { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' }, { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' }, { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' },
]

const PSC_CATEGORIES: { code: string; name: string }[] = Object.entries(PSC_L1_NAMES)
  .map(([code, name]) => ({ code, name }))
  .sort((a, b) => Number(a.code) - Number(b.code))

const PSC_L2_CATEGORIES: { code: string; name: string }[] = Object.entries(PSC_L2_NAMES)
  .map(([code, name]) => ({ code, name }))
  .sort((a, b) => {
    const [a1, a2] = a.code.split('.').map(Number)
    const [b1, b2] = b.code.split('.').map(Number)
    return a1 - b1 || a2 - b2
  })

// ── Reusable typeahead multi-select ────────────────────────────────

function TypeaheadMultiSelect({
  options,
  selected,
  onChange,
  placeholder,
}: {
  options: { code: string; name: string }[]
  selected: string[]
  onChange: (codes: string[]) => void
  placeholder: string
}) {
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const nameMap = useMemo(() => {
    const m = new Map<string, string>()
    for (const o of options) m.set(o.code, o.name)
    return m
  }, [options])

  const filtered = search
    ? options.filter(
        (s) =>
          !selected.includes(s.code) &&
          (s.code.toLowerCase().includes(search.toLowerCase()) ||
            s.name.toLowerCase().includes(search.toLowerCase())),
      )
    : options.filter((s) => !selected.includes(s.code))

  const toggle = (code: string) => {
    if (selected.includes(code)) {
      onChange(selected.filter((s) => s !== code))
    } else {
      onChange([...selected, code])
      setSearch('')
    }
  }

  return (
    <div ref={ref} className="relative">
      <div
        className="min-h-[36px] px-2 py-1 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex flex-wrap gap-1 cursor-text"
        onClick={() => setOpen(true)}
      >
        {selected.map((code) => (
          <span
            key={code}
            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs"
          >
            {nameMap.get(code) ?? code}
            <button onClick={(e) => { e.stopPropagation(); toggle(code) }} className="hover:text-blue-900">
              <X size={10} />
            </button>
          </span>
        ))}
        <input
          value={search}
          onChange={(e) => { setSearch(e.target.value); setOpen(true) }}
          onFocus={() => setOpen(true)}
          placeholder={selected.length === 0 ? placeholder : ''}
          className="flex-1 min-w-[60px] bg-transparent outline-none text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400"
        />
      </div>

      {open && (
        <div className="absolute z-20 mt-1 w-full max-h-48 overflow-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg">
          {selected.length > 0 && (
            <button
              onClick={() => { onChange([]); setSearch('') }}
              className="w-full text-left px-3 py-1.5 text-xs text-red-500 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Clear all
            </button>
          )}
          {filtered.length === 0 && (
            <div className="px-3 py-2 text-xs text-gray-400">No matches</div>
          )}
          {filtered.map((s) => (
            <button
              key={s.code}
              onClick={() => toggle(s.code)}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 dark:hover:bg-blue-900/20 flex items-center justify-between"
            >
              <span className="text-gray-900 dark:text-gray-100 truncate">{s.name}</span>
              <span className="text-xs text-gray-400 shrink-0 ml-2">{s.code}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Main component ─────────────────────────────────────────────────

export default function ExecDIBExplore() {
  const [metric, setMetric] = useState<MetricKey>('total_obligated')
  const [groupBy, setGroupBy] = useState<GroupByDimension>('state')
  const [seriesBy, setSeriesBy] = useState<GroupByDimension | ''>('')
  const [chartType, setChartType] = useState<ChartType>('map')
  const [stateFilter, setStateFilter] = useState<string[]>([])
  const [pscFilter, setPscFilter] = useState<string[]>([])
  const [psc2Filter, setPsc2Filter] = useState<string[]>([])
  const [fyRange, setFyRange] = useState<string>('')
  const [locationKind, setLocationKind] = useState<LocationKind>('recipient')
  const [limit, setLimit] = useState(DEFAULT_LIMIT)
  const [extrasOpen, setExtrasOpen] = useState(false)
  const [truncationOpen, setTruncationOpen] = useState(false)
  const truncationRef = useRef<HTMLDivElement>(null)

  const [drillStack, setDrillStack] = useState<{ dim: GroupByDimension; code: string; label: string }[]>([])

  const effectiveGroupBy = drillStack.length > 0
    ? drillStack[drillStack.length - 1].dim
    : groupBy

  const handleGroupByChange = (dim: GroupByDimension) => {
    setGroupBy(dim)
    setDrillStack([])
    // PSC L2 lives in geo endpoint but map renderer doesn't support it — fall back to bar.
    const mapCapable = GEO_DIMENSIONS.has(dim) && dim !== 'psc_level2_code'
    if (mapCapable && chartType !== 'map') {
      setChartType('map')
    } else if (!mapCapable && chartType === 'map') {
      setChartType('bar')
    }
  }

  const handleSwap = () => {
    if (!seriesBy) return
    const oldGroupBy = groupBy
    const oldSeriesBy = seriesBy as GroupByDimension
    setGroupBy(oldSeriesBy)
    setSeriesBy(oldGroupBy)
    setDrillStack([])
    const mapCapable = GEO_DIMENSIONS.has(oldSeriesBy) && oldSeriesBy !== 'psc_level2_code'
    if (mapCapable && chartType !== 'map') {
      setChartType('map')
    } else if (!mapCapable && chartType === 'map') {
      setChartType('bar')
    }
  }

  const config = useMemo((): GenericWidgetConfig => ({
    group_by: effectiveGroupBy,
    metrics: [metric],
    chart_type: chartType,
    series_by: (drillStack.length > 0 ? undefined : seriesBy || undefined) as GroupByDimension | undefined,
    state_code: stateFilter.length > 0 ? stateFilter : undefined,
    psc_level1_code: pscFilter.length === 1 ? pscFilter[0] : undefined,
    psc_level2_code: psc2Filter.length === 1 ? psc2Filter[0] : undefined,
    location_kind: locationKind,
    fiscal_year: fyRange || undefined,
    limit,
  }), [effectiveGroupBy, metric, chartType, seriesBy, stateFilter, pscFilter, psc2Filter, locationKind, fyRange, limit, drillStack])

  const drillFilters = useMemo((): Record<string, string | number> | undefined => {
    if (drillStack.length === 0) return undefined
    const top = drillStack[drillStack.length - 1]
    if (top.dim === 'congressional_district') {
      return { state_code: top.code }
    }
    if (top.dim === 'fiscal_year') {
      return { congressional_district: top.code }
    }
    if (top.dim === 'psc_level2_code') {
      return { psc_level1_code: top.code }
    }
    return undefined
  }, [drillStack])

  const { data, loading, refresh } = usePerformanceQuery(config, drillFilters)

  // Heuristic: if we received exactly `limit` rows, results were likely truncated.
  const isTruncated = !loading && data.length >= limit

  // Close the popover if the user changes filters and the signal clears.
  useEffect(() => {
    if (!isTruncated) setTruncationOpen(false)
  }, [isTruncated])

  // Dismiss truncation popover on outside click.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (truncationRef.current && !truncationRef.current.contains(e.target as Node)) {
        setTruncationOpen(false)
      }
    }
    if (truncationOpen) {
      document.addEventListener('mousedown', handler)
      return () => document.removeEventListener('mousedown', handler)
    }
  }, [truncationOpen])

  // ── Preset example queries ─────────────────────────────────────────
  type Preset = { key: string; label: string; apply: () => void }
  const applyPreset = useCallback((preset: Preset) => {
    preset.apply()
    setDrillStack([])
    setTruncationOpen(false)
    trackEvent('dib_explore_preset_clicked', { preset: preset.key })
  }, [])

  const PRESETS: Preset[] = useMemo(() => [
    {
      key: 'states_by_dollars',
      label: 'States by obligated dollars',
      apply: () => {
        setMetric('total_obligated')
        setGroupBy('state')
        setSeriesBy('')
        setChartType('map')
        setStateFilter([])
        setPscFilter([])
        setPsc2Filter([])
        setFyRange('')
        setLocationKind('recipient')
      },
    },
    {
      key: 'missile_trend',
      label: 'Missile contracts over time',
      apply: () => {
        setMetric('award_count')
        setGroupBy('fiscal_year')
        setSeriesBy('')
        setChartType('line')
        setStateFilter([])
        setPscFilter([])
        setPsc2Filter(['12.3'])
        setFyRange('')
        setLocationKind('recipient')
      },
    },
    {
      key: 'pa_8a_by_cd',
      label: '8(a) by district in Pennsylvania',
      apply: () => {
        setMetric('count_8a')
        setGroupBy('congressional_district')
        setSeriesBy('')
        setChartType('map')
        setStateFilter(['PA'])
        setPscFilter([])
        setPsc2Filter([])
        setFyRange('')
        setLocationKind('recipient')
      },
    },
    {
      key: 'vendor_trend_top_states',
      label: 'Vendor trends in top 10 states',
      apply: () => {
        setMetric('vendor_count')
        setGroupBy('fiscal_year')
        setSeriesBy('state')
        setChartType('line')
        setStateFilter(['CA', 'TX', 'VA', 'MD', 'FL', 'PA', 'AL', 'NY', 'OH', 'MA'])
        setPscFilter([])
        setPsc2Filter([])
        setFyRange('')
        setLocationKind('recipient')
      },
    },
  ], [])

  const queryTracked = useRef<string>('')
  useEffect(() => {
    if (loading || !data) return
    const key = JSON.stringify(config) + JSON.stringify(drillFilters ?? '')
    if (key === queryTracked.current) return
    queryTracked.current = key
    trackEvent('dib_explore_query_executed', {
      metric,
      group_by: effectiveGroupBy,
      chart_type: chartType,
      location_kind: locationKind,
      state_codes: stateFilter.length > 0 ? stateFilter.join(',') : null,
      drill_depth: drillStack.length,
      result_count: data?.length ?? 0,
    })
  }, [loading, data, config, drillFilters, metric, effectiveGroupBy, chartType, locationKind, stateFilter, drillStack.length])

  const canDrill = !!DRILL_HIERARCHY[effectiveGroupBy]

  const handleDrillDown = (code: string, label: string) => {
    const next = DRILL_HIERARCHY[effectiveGroupBy]
    if (!next) return
    trackEvent('dib_explore_drill_down', {
      from_dimension: effectiveGroupBy, to_dimension: next, code, label, metric,
    })
    setDrillStack((prev) => [...prev, { dim: next, code, label }])
  }

  const handleBreadcrumbClick = (index: number) => {
    if (index < 0) setDrillStack([])
    else setDrillStack((prev) => prev.slice(0, index + 1))
  }

  // Add current view to dashboard
  const [addingToDashboard, setAddingToDashboard] = useState(false)
  const [addedToDashboard, setAddedToDashboard] = useState(false)

  const handleAddToDashboard = useCallback(async () => {
    setAddingToDashboard(true)
    try {
      const dashboards = await execAPI.getDashboards()
      let dashboardId: string
      if (dashboards.length > 0) {
        dashboardId = dashboards[0].id
      } else {
        const created = await execAPI.createDashboard({ name: 'DIB Dashboard', is_default: true })
        dashboardId = created.id
      }

      const widgetConfig: Record<string, unknown> = {
        group_by: groupBy,
        metrics: [metric],
        chart_type: chartType,
        location_kind: locationKind,
      }
      if (seriesBy) widgetConfig.series_by = seriesBy
      if (stateFilter.length === 1) widgetConfig.state_code = stateFilter[0]
      else if (stateFilter.length > 1) widgetConfig.state_code = stateFilter
      if (pscFilter.length > 0) widgetConfig.psc_level1_code = pscFilter[0]
      if (psc2Filter.length > 0) widgetConfig.psc_level2_code = psc2Filter[0]
      if (fyRange) widgetConfig.fiscal_year = fyRange
      if (limit !== DEFAULT_LIMIT) widgetConfig.limit = limit

      const title = `${METRIC_LABELS[metric]} by ${DIMENSION_LABELS[groupBy]}${
        stateFilter.length > 0 ? ` (${stateFilter.join(', ')})` : ''
      }`

      const isWide = chartType === 'heatmap' || chartType === 'table' || chartType === 'map'
      await execAPI.addWidget(dashboardId, {
        measure: 'geo_performance',
        config: widgetConfig,
        title,
      })

      const dash = await execAPI.getDashboard(dashboardId)
      const maxY = dash.layout.reduce((max: number, item: { y: number; h: number }) => Math.max(max, item.y + item.h), 0)
      const newWidget = dash.widgets[dash.widgets.length - 1]
      if (newWidget) {
        await execAPI.updateLayout(dashboardId, [
          ...dash.layout,
          { i: newWidget.id, x: 0, y: maxY, w: isWide ? 12 : 6, h: chartType === 'map' ? 7 : 5 },
        ])
      }

      setAddedToDashboard(true)
      setTimeout(() => setAddedToDashboard(false), 2000)
    } catch (err) {
      console.error('Failed to add to dashboard:', err)
    } finally {
      setAddingToDashboard(false)
    }
  }, [groupBy, metric, chartType, seriesBy, stateFilter, pscFilter, psc2Filter, locationKind, fyRange, limit])

  const selectClass = 'w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500'

  return (
    <div className="flex h-[calc(var(--app-height)-4rem)]">
      {/* Controls sidebar */}
      <div className="w-72 shrink-0 border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-y-auto p-4 space-y-5">
        <div className="flex items-center gap-2 mb-2">
          <Globe size={18} className="text-blue-500" />
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">DIB Explorer</h2>
        </div>

        {/* ── Metric ─────────────────────────────────────────── */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Metric</label>
          <select value={metric} onChange={(e) => setMetric(e.target.value as MetricKey)} className={selectClass}>
            {METRIC_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.group}: {opt.label}</option>
            ))}
          </select>
        </div>

        {/* ── Group By ───────────────────────────────────────── */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Group By</label>
          <select
            value={groupBy}
            onChange={(e) => handleGroupByChange(e.target.value as GroupByDimension)}
            className={selectClass}
          >
            {GROUP_BY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Swap button */}
        {seriesBy && (
          <div className="flex justify-center -my-2">
            <button
              onClick={handleSwap}
              title="Swap Group By and Compare By"
              className="p-1 rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-400 hover:text-blue-600 hover:border-blue-300 transition-colors"
            >
              <ArrowLeftRight size={14} />
            </button>
          </div>
        )}

        {/* ── Compare By ─────────────────────────────────────── */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Compare By</label>
          <select
            value={seriesBy}
            onChange={(e) => setSeriesBy(e.target.value as GroupByDimension | '')}
            className={selectClass}
          >
            <option value="">None</option>
            {GROUP_BY_OPTIONS.filter((o) => o.value !== groupBy).map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* ── Chart Type ─────────────────────────────────────── */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Chart Type</label>
          <div className="grid grid-cols-5 gap-1">
            {CHART_TYPES.map((ct) => (
              <button
                key={ct.value}
                onClick={() => setChartType(ct.value)}
                className={`flex flex-col items-center gap-1 p-2 rounded-lg text-xs border transition-colors ${
                  chartType === ct.value
                    ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                    : 'border-gray-200 dark:border-gray-700 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                {ct.icon}
                {ct.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Filters heading ────────────────────────────────── */}
        <div className="pt-1">
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Filters</h3>

          {/* Fiscal Year range */}
          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-500 mb-2">Fiscal Year</label>
            <FYRangeSlider value={fyRange} onChange={setFyRange} />
          </div>

          {/* State multi-select */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">States</label>
            <TypeaheadMultiSelect
              options={US_STATES}
              selected={stateFilter}
              onChange={(states) => { setStateFilter(states); setDrillStack([]) }}
              placeholder="All states"
            />
          </div>

          {/* PSC multi-select */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">PSC Category</label>
            <TypeaheadMultiSelect
              options={PSC_CATEGORIES}
              selected={pscFilter}
              onChange={setPscFilter}
              placeholder="All categories"
            />
          </div>

          {/* PSC L2 multi-select */}
          <div className="mt-3">
            <label className="block text-xs font-medium text-gray-500 mb-1">PSC Category (L2)</label>
            <TypeaheadMultiSelect
              options={PSC_L2_CATEGORIES}
              selected={psc2Filter}
              onChange={setPsc2Filter}
              placeholder="All L2 categories"
            />
          </div>
        </div>

        {/* ── Extras (collapsible) ───────────────────────────── */}
        <div className="pt-1">
          <button
            onClick={() => setExtrasOpen(!extrasOpen)}
            className="flex items-center gap-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider hover:text-gray-600 transition-colors w-full"
          >
            <ChevronDown
              size={12}
              className={`transition-transform ${extrasOpen ? '' : '-rotate-90'}`}
            />
            Extras
          </button>

          {extrasOpen && (
            <div className="mt-3 space-y-4">
              {/* Attribution */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Attribution</label>
                <div className="grid grid-cols-2 gap-1">
                  {([['recipient', 'Recipient HQ'], ['pop', 'Place of Perf.']] as const).map(([val, lab]) => (
                    <button
                      key={val}
                      onClick={() => setLocationKind(val)}
                      className={`px-2 py-1.5 text-xs rounded-lg border transition-colors ${
                        locationKind === val
                          ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                          : 'border-gray-200 dark:border-gray-700 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                    >
                      {lab}
                    </button>
                  ))}
                </div>
              </div>

              {/* Max Results */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Max Results: {limit}</label>
                <input
                  type="range"
                  min={MIN_LIMIT}
                  max={MAX_LIMIT}
                  step={LIMIT_STEP}
                  value={limit}
                  onChange={(e) => setLimit(Number(e.target.value))}
                  className="w-full"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Chart area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Breadcrumbs + title */}
        <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <button onClick={() => handleBreadcrumbClick(-1)} className="hover:text-blue-600">
                {DIMENSION_LABELS[groupBy]}
              </button>
              {drillStack.map((level, i) => (
                <span key={i} className="flex items-center gap-1">
                  <span className="text-gray-300">/</span>
                  <button onClick={() => handleBreadcrumbClick(i)} className="hover:text-blue-600">
                    {level.label}
                  </button>
                </span>
              ))}
            </div>
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mt-0.5">
              {METRIC_LABELS[metric]} by {DIMENSION_LABELS[effectiveGroupBy]}
              {stateFilter.length > 0 ? ` \u2014 ${stateFilter.join(', ')}` : ''}
              {seriesBy && !drillStack.length ? `, compared by ${DIMENSION_LABELS[seriesBy as GroupByDimension]}` : ''}
              <span className="ml-2 text-xs text-gray-400 font-normal">
                ({locationKind === 'recipient' ? 'Recipient HQ' : 'Place of Performance'})
              </span>
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">
              {data.length} result{data.length !== 1 ? 's' : ''}
            </span>
            {isTruncated && (
              <div ref={truncationRef} className="relative">
                <button
                  type="button"
                  aria-label="Results may be truncated"
                  onClick={() => {
                    setTruncationOpen((v) => !v)
                    setExtrasOpen(true)
                  }}
                  className="flex items-center justify-center w-5 h-5 rounded-full border-2 border-red-500 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                >
                  <AlertCircle size={12} strokeWidth={2.5} />
                </button>
                {truncationOpen && (
                  <div className="absolute right-0 top-6 z-20 w-64 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg text-xs text-gray-700 dark:text-gray-200">
                    <div className="font-medium text-red-600 dark:text-red-400 mb-1">
                      Results may be truncated
                    </div>
                    <div>
                      Showing {limit} row{limit === 1 ? '' : 's'} — the cap was hit, so some rows may be hidden. Narrow your filters or raise the <span className="font-medium">Max Results</span> slider (up to {MAX_LIMIT}).
                    </div>
                  </div>
                )}
              </div>
            )}
            <button
              onClick={handleAddToDashboard}
              disabled={addingToDashboard}
              title="Add to dashboard"
              className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${
                addedToDashboard
                  ? 'bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400'
                  : 'text-gray-500 hover:text-blue-600 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
            >
              {addedToDashboard ? <Check size={12} /> : <LayoutDashboard size={12} />}
              {addingToDashboard ? 'Saving...' : addedToDashboard ? 'Added' : 'Add to Dashboard'}
            </button>
            <button
              onClick={refresh}
              title="Refresh data"
              className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <RefreshCw size={14} />
            </button>
          </div>
        </div>

        {/* Preset example queries */}
        <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-800 flex items-center gap-2 overflow-x-auto">
          <span className="flex items-center gap-1 text-xs text-gray-400 shrink-0">
            <Sparkles size={12} />
            Try:
          </span>
          {PRESETS.map((preset) => (
            <button
              key={preset.key}
              onClick={() => applyPreset(preset)}
              className="shrink-0 px-2.5 py-1 text-xs rounded-full border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
            >
              {preset.label}
            </button>
          ))}
        </div>

        {/* Chart */}
        <div className="flex-1 min-h-0 relative p-2">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/60 dark:bg-gray-900/60 z-10">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            </div>
          )}

          {chartType === 'map' && (
            <MapChartRenderer data={data} groupBy={effectiveGroupBy} metrics={config.metrics} onDrillDown={canDrill ? handleDrillDown : undefined} stateFilter={stateFilter.length === 1 ? stateFilter[0] : undefined} />
          )}
          {chartType === 'bar' && (
            <BarChartRenderer data={data} groupBy={effectiveGroupBy} metrics={config.metrics} seriesBy={config.series_by} onDrillDown={canDrill ? handleDrillDown : undefined} />
          )}
          {chartType === 'line' && (
            <LineChartRenderer data={data} groupBy={effectiveGroupBy} metrics={config.metrics} seriesBy={config.series_by} />
          )}
          {chartType === 'heatmap' && (
            <HeatmapRenderer data={data} groupBy={effectiveGroupBy} metrics={config.metrics} seriesBy={config.series_by} onDrillDown={canDrill ? handleDrillDown : undefined} />
          )}
          {chartType === 'table' && (
            <TableRenderer data={data} groupBy={effectiveGroupBy} metrics={config.metrics} seriesBy={config.series_by} onDrillDown={canDrill ? handleDrillDown : undefined} />
          )}
        </div>
      </div>
    </div>
  )
}
